// ── SALES TAX CODES ───────────────────────────────────────────────────────────
// Finance → State Tax (top). Firm-level jurisdiction codes, one per place
// the firm delivers into: a header (name / code / state), the authorities
// that stack into the blended rate, and the taxable-category defaults.
// Mirrors the Design Manager "Sales Tax Code" screen Cimon kept.
//
// A code is a TEMPLATE. The project form's "Tax code" picker copies the
// blended rate + flags into the project's own tax fields; nothing here
// is read at tax time, so editing a code never restates a filed month.
// Projects that were set up by hand before codes existed stay as they
// are.
//
// Color rules per BRAND: white ground, hairline borders, no fills.
import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { US_STATES } from './StateTaxSection';
import { salesTaxCodeBlendedRate, salesTaxCodeReturns, projectMatchesTaxCode, TAX_CODE_FLAG_KEYS, hasAdminAccess } from '../utils';

var FLAG_LABELS = {
  taxesMerchandise: 'Merchandise', taxesFreight: 'Freight', taxesCrating: 'Crating',
  taxesInstall: 'Install / labor', taxesTariff: 'Tariff / duty', taxesStorage: 'Storage', taxesTime: 'Time / hours',
};

var COLOR_TEXT = '#111111', COLOR_MUTED = '#888880', COLOR_DK = '#D8D7D2', COLOR_ALERT = '#D4007A';

var inp = {
  width: '100%', padding: '7px 10px', fontSize: 13, fontFamily: 'inherit',
  border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff',
  outline: 'none', boxSizing: 'border-box',
};
var lbl = { fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4, display: 'block' };
var th  = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: COLOR_MUTED, whiteSpace: 'nowrap', borderBottom: '0.5px solid ' + COLOR_DK, background: '#fff', position: 'sticky', top: 0, zIndex: 2, boxShadow: '0 0.5px 0 ' + COLOR_DK };
var td  = { padding: '8px 12px', fontSize: 12, color: COLOR_TEXT, borderBottom: '0.5px solid #EEEEEA', verticalAlign: 'top' };
var btnGhost   = { height: 28, padding: '0 12px', borderRadius: 14, border: '0.5px solid #E0E0DC', background: '#fff', color: '#111', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnPrimary = { height: 28, padding: '0 14px', borderRadius: 14, border: 'none', background: '#111', color: '#fff', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnDanger  = { height: 28, padding: '0 12px', borderRadius: 14, border: '0.5px solid #C8322A', background: '#fff', color: '#C8322A', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };

function pct(rate) {
  var n = parseFloat(rate);
  if (isNaN(n)) return '';
  return String(Math.round(n * 100000) / 1000);
}
function pctToRate(v) {
  var t = String(v == null ? '' : v).replace(/[%\s]/g, '');
  if (t === '') return '';
  var n = parseFloat(t);
  return isNaN(n) ? '' : Math.round(n * 10000) / 1000000;
}
function taxedLabels(c) {
  return TAX_CODE_FLAG_KEYS.filter(function(k) { return k === 'taxesMerchandise' ? c[k] !== false : !!c[k]; })
    .map(function(k) { return FLAG_LABELS[k]; });
}

var BLANK = {
  id: null, code: '', name: '', stateCode: '', authorities: [{ name: '', rate: '', returnKey: '' }], returns: [],
  taxesMerchandise: true, taxesFreight: false, taxesCrating: false, taxesInstall: false,
  taxesTariff: false, taxesStorage: false, taxesTime: false, printDescription: 'Sales Tax', active: true, notes: '',
};

export default function SalesTaxCodesSection() {
  var ctx = useAppContext();
  var codes    = ctx.salesTaxCodes || [];
  var projects = ctx.projects || [];
  var save     = ctx.saveSalesTaxCode;
  var remove   = ctx.deleteSalesTaxCode;
  var canWrite = !!(ctx.isOwner || hasAdminAccess(ctx.userEmail, ctx.firmSettings));

  var ms = useState(null); var modal = ms[0]; var setModal = ms[1];
  var es = useState('');   var err   = es[0]; var setErr   = es[1];

  var usage = useMemo(function() {
    var m = {};
    projects.forEach(function(p) {
      if (!p.taxCodeId) return;
      var code = codes.find(function(c) { return c.id === p.taxCodeId; });
      if (!m[p.taxCodeId]) m[p.taxCodeId] = { total: 0, drifted: 0 };
      m[p.taxCodeId].total += 1;
      if (code && !projectMatchesTaxCode(p, code)) m[p.taxCodeId].drifted += 1;
    });
    return m;
  }, [projects, codes]);

  function openNew() { setErr(''); setModal(Object.assign({}, BLANK, { authorities: [{ name: '', rate: '', returnKey: '' }], returns: [] })); }
  function openEdit(c) {
    setErr('');
    setModal(Object.assign({}, c, {
      authorities: (c.authorities || []).map(function(a) { return { name: a.name, rate: a.rate, returnKey: a.returnKey || '' }; }),
      returns: (c.returns || []).map(function(r) { return { key: r.key, label: r.label, jurisdiction: r.jurisdiction }; }),
    }));
  }
  // Returns (2026-10-16): the extra portals a code files beyond the state
  // return. Each authority then says which return it is paid on.
  function setRet(i, k, v) {
    setModal(function(m) {
      var list = (m.returns || []).slice();
      list[i] = Object.assign({}, list[i]); list[i][k] = k === 'key' ? String(v || '').toUpperCase() : v;
      return Object.assign({}, m, { returns: list });
    });
  }
  function addRet() { setModal(function(m) { return Object.assign({}, m, { returns: (m.returns || []).concat([{ key: '', label: '', jurisdiction: '' }]) }); }); }
  function dropRet(i) {
    setModal(function(m) {
      var gone = (m.returns || [])[i];
      var returns = (m.returns || []).filter(function(_r, j) { return j !== i; });
      var authorities = m.authorities.map(function(a) { return gone && a.returnKey === gone.key ? Object.assign({}, a, { returnKey: '' }) : a; });
      return Object.assign({}, m, { returns: returns, authorities: authorities });
    });
  }
  function setField(k) { return function(v) { setModal(function(m) { var n = Object.assign({}, m); n[k] = v; return n; }); }; }
  function setAuth(i, k, v) {
    setModal(function(m) {
      var list = m.authorities.slice();
      list[i] = Object.assign({}, list[i]); list[i][k] = v;
      return Object.assign({}, m, { authorities: list });
    });
  }
  function addAuth() { setModal(function(m) { return Object.assign({}, m, { authorities: m.authorities.concat([{ name: '', rate: '', returnKey: '' }]) }); }); }
  function dropAuth(i) { setModal(function(m) { return Object.assign({}, m, { authorities: m.authorities.filter(function(_a, j) { return j !== i; }) }); }); }

  function onSave() {
    var code = String(modal.code || '').trim().toUpperCase();
    if (!code) { setErr('Code is required.'); return; }
    if (!String(modal.name || '').trim()) { setErr('Name is required.'); return; }
    if (!modal.stateCode) { setErr('State is required.'); return; }
    var dup = codes.some(function(c) { return c.id !== modal.id && String(c.code).toUpperCase() === code; });
    if (dup) { setErr('Code ' + code + ' already exists.'); return; }
    var authorities = (modal.authorities || [])
      .filter(function(a) { return String(a.name || '').trim() !== '' || a.rate !== '' && a.rate != null; })
      .map(function(a) { return { name: String(a.name || '').trim(), rate: parseFloat(a.rate) || 0, returnKey: String(a.returnKey || '').trim().toUpperCase() }; });
    if (!authorities.length) { setErr('Add at least one authority with a rate.'); return; }
    if (authorities.some(function(a) { return !a.name; })) { setErr('Every authority needs a name.'); return; }
    var returns = (modal.returns || []).map(function(r) { return { key: String(r.key || '').trim().toUpperCase(), label: String(r.label || '').trim(), jurisdiction: String(r.jurisdiction || '').trim() }; })
      .filter(function(r) { return r.key || r.label; });
    if (returns.some(function(r) { return !r.key || !r.label; })) { setErr('Every return needs a key and a name.'); return; }
    var keys = {}; if (returns.some(function(r) { if (keys[r.key]) return true; keys[r.key] = true; return false; })) { setErr('Return keys must be unique.'); return; }
    save(Object.assign({}, modal, { code: code, authorities: authorities, returns: returns }));
    setModal(null);
  }
  function onDelete() {
    var u = usage[modal.id];
    var msg = 'Delete tax code ' + modal.code + '?' + (u ? ' ' + u.total + ' project' + (u.total === 1 ? '' : 's') + ' reference it; their own tax settings stay as they are.' : '');
    if (!window.confirm(msg)) return;
    remove(modal.id);
    setModal(null);
  }

  var blended = modal ? salesTaxCodeBlendedRate(modal) : 0;

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: COLOR_TEXT }}>Sales Tax Codes</div>
        <div style={{ fontSize: 11, color: COLOR_MUTED }}>One per jurisdiction. Authorities add up to the rate. Projects copy a code; editing a code changes no project.</div>
        {canWrite && <button style={Object.assign({}, btnPrimary, { marginLeft: 'auto' })} onClick={openNew}>+ Add code</button>}
      </div>

      <div style={{ border: '0.5px solid ' + COLOR_DK, borderRadius: 8, overflow: 'auto', maxHeight: 420 }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead><tr>
            <th style={th}>Code</th><th style={th}>Name</th><th style={th}>State</th>
            <th style={th}>Authorities</th><th style={Object.assign({}, th, { textAlign: 'right' })}>Rate</th>
            <th style={th}>Taxed here</th><th style={th}>Projects</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td style={Object.assign({}, td, { color: COLOR_MUTED })} colSpan={8}>No tax codes yet. Add one per jurisdiction you deliver into.</td></tr>
            )}
            {codes.map(function(c) {
              var u = usage[c.id];
              return (
                <tr key={c.id} style={{ opacity: c.active === false ? 0.5 : 1 }}>
                  <td style={Object.assign({}, td, { fontWeight: 600, whiteSpace: 'nowrap' })}>{c.code}{c.active === false ? <span style={{ color: COLOR_MUTED, fontWeight: 400 }}> · inactive</span> : null}</td>
                  <td style={td}>{c.name}</td>
                  <td style={td}>{c.stateCode}</td>
                  <td style={td}>
                    {(c.authorities || []).map(function(a, i) {
                      return <div key={i} style={{ display: 'flex', gap: 10, whiteSpace: 'nowrap' }}><span>{a.name}</span><span style={{ color: COLOR_MUTED, marginLeft: 'auto' }}>{pct(a.rate)}%</span></div>;
                    })}
                  </td>
                  <td style={Object.assign({}, td, { textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' })}>
                    {pct(salesTaxCodeBlendedRate(c))}%
                    {(c.returns || []).length > 0 && salesTaxCodeReturns(c).map(function(r) {
                      return <div key={r.key || 'STATE'} style={{ fontSize: 10, fontWeight: 400, color: COLOR_MUTED }} data-testid={'tax-code-return-' + c.code + '-' + (r.key || 'STATE')}>{r.label} {pct(r.rate)}%</div>;
                    })}
                  </td>
                  <td style={td}>{taxedLabels(c).join(', ') || <span style={{ color: COLOR_MUTED }}>none</span>}</td>
                  <td style={Object.assign({}, td, { whiteSpace: 'nowrap' })}>
                    {u ? u.total : <span style={{ color: COLOR_MUTED }}>—</span>}
                    {u && u.drifted > 0 ? <span style={{ color: COLOR_ALERT, marginLeft: 6 }} title="A project that copied this code has since been edited by hand.">{u.drifted} differ</span> : null}
                  </td>
                  <td style={Object.assign({}, td, { textAlign: 'right' })}>{canWrite && <button style={btnGhost} onClick={function() { openEdit(c); }}>Edit</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={function() { setModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 640, maxWidth: '94vw', maxHeight: '90vh', overflow: 'auto', border: '0.5px solid ' + COLOR_DK }} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{modal.id ? 'Edit tax code' : 'New tax code'}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div><label style={lbl}>Name</label><input style={inp} value={modal.name} onChange={function(e) { setField('name')(e.target.value); }} placeholder="Vail, Colorado" /></div>
              <div><label style={lbl}>Code</label><input style={inp} value={modal.code} onChange={function(e) { setField('code')(e.target.value.toUpperCase()); }} placeholder="VAIL" /></div>
              <div><label style={lbl}>State</label>
                <select style={inp} value={modal.stateCode} onChange={function(e) { setField('stateCode')(e.target.value); }}>
                  <option value="">—</option>
                  {US_STATES.map(function(s) { return <option key={s[0]} value={s[0]}>{s[1]}</option>; })}
                </select>
              </div>
            </div>

            <label style={lbl}>Authorities · rates add up</label>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
              <tbody>
                {modal.authorities.map(function(a, i) {
                  return (
                    <tr key={i}>
                      <td style={{ padding: '3px 4px 3px 0' }}><input style={inp} value={a.name} onChange={function(e) { setAuth(i, 'name', e.target.value); }} placeholder="State of Wyoming" /></td>
                      <td style={{ padding: 3, width: 110 }}>
                        <div style={{ position: 'relative' }}>
                          <input style={Object.assign({}, inp, { textAlign: 'right', paddingRight: 22 })} value={pct(a.rate)} onChange={function(e) { setAuth(i, 'rate', pctToRate(e.target.value)); }} placeholder="4" />
                          <span style={{ position: 'absolute', right: 8, top: 8, fontSize: 12, color: COLOR_MUTED }}>%</span>
                        </div>
                      </td>
                      {(modal.returns || []).length > 0 && (
                        <td style={{ padding: 3, width: 170 }}>
                          <select style={inp} value={a.returnKey || ''} onChange={function(e) { setAuth(i, 'returnKey', e.target.value); }} title="Which return this authority is paid on" data-testid={'tax-code-paid-on-' + i}>
                            <option value="">State return</option>
                            {(modal.returns || []).filter(function(r) { return r.key; }).map(function(r) { return <option key={r.key} value={r.key}>{r.label || r.key}</option>; })}
                          </select>
                        </td>
                      )}
                      <td style={{ padding: 3, width: 32, textAlign: 'right' }}>
                        <button title="Remove" style={{ border: 'none', background: 'none', color: COLOR_MUTED, cursor: 'pointer', fontSize: 14 }} onClick={function() { dropAuth(i); }}>×</button>
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ padding: '6px 4px 6px 0' }}><button style={btnGhost} onClick={addAuth}>+ Add authority</button></td>
                  <td style={{ padding: 3, textAlign: 'right', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }} data-testid="tax-code-blended">{pct(blended)}%</td>
                  {(modal.returns || []).length > 0 && <td></td>}
                  <td></td>
                </tr>
              </tbody>
            </table>

            <label style={Object.assign({}, lbl, { marginTop: 10 })}>Returns · beyond the state return</label>
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginBottom: 6 }}>
              A home-rule city that collects its own tax (Vail) is its own return. Add it here, then mark its authority &ldquo;Paid on&rdquo; that return above. The rate charged to clients does not change; only where the collected tax is paid.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
              <tbody>
                {(modal.returns || []).map(function(r, i) {
                  return (
                    <tr key={i}>
                      <td style={{ padding: '3px 4px 3px 0', width: 110 }}><input style={inp} value={r.key} onChange={function(e) { setRet(i, 'key', e.target.value); }} placeholder="VAIL" data-testid={'tax-code-return-key-' + i} /></td>
                      <td style={{ padding: 3 }}><input style={inp} value={r.label} onChange={function(e) { setRet(i, 'label', e.target.value); }} placeholder="Town of Vail" /></td>
                      <td style={{ padding: 3, width: 170 }}><input style={inp} value={r.jurisdiction} onChange={function(e) { setRet(i, 'jurisdiction', e.target.value); }} placeholder="Vail" title="The city on the State Tax record that holds this return's portal credentials and due rule" /></td>
                      <td style={{ padding: 3, width: 32, textAlign: 'right' }}>
                        <button title="Remove" style={{ border: 'none', background: 'none', color: COLOR_MUTED, cursor: 'pointer', fontSize: 14 }} onClick={function() { dropRet(i); }}>×</button>
                      </td>
                    </tr>
                  );
                })}
                <tr><td colSpan={4} style={{ padding: '6px 4px 6px 0' }}><button style={btnGhost} onClick={addRet}>+ Add return</button>{(modal.returns || []).length > 0 && <span style={{ fontSize: 11, color: COLOR_MUTED, marginLeft: 10 }}>key · name · city on the State Tax record</span>}</td></tr>
              </tbody>
            </table>
            {(modal.returns || []).length > 0 && (
              <div style={{ fontSize: 11, color: COLOR_TEXT, marginBottom: 10 }} data-testid="tax-code-return-split">
                {salesTaxCodeReturns(modal).map(function(r) { return r.label + ' ' + pct(r.rate) + '%'; }).join(' + ')} = {pct(blended)}%
              </div>
            )}

            <label style={Object.assign({}, lbl, { marginTop: 10 })}>Taxable in this jurisdiction</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 14 }}>
              {TAX_CODE_FLAG_KEYS.map(function(k) {
                return (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#333', cursor: 'pointer' }}>
                    <input type="checkbox" checked={k === 'taxesMerchandise' ? modal[k] !== false : !!modal[k]} onChange={function(e) { setField(k)(e.target.checked); }} style={{ width: 14, height: 14 }} />
                    <span>{FLAG_LABELS[k]}</span>
                  </label>
                );
              })}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div><label style={lbl}>Printed as</label><input style={inp} value={modal.printDescription} onChange={function(e) { setField('printDescription')(e.target.value); }} /></div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#333', cursor: 'pointer' }}>
                  <input type="checkbox" checked={modal.active === false} onChange={function(e) { setField('active')(!e.target.checked); }} style={{ width: 14, height: 14 }} />
                  <span>Inactive · hidden from the project picker</span>
                </label>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}><label style={lbl}>Notes</label><textarea style={Object.assign({}, inp, { minHeight: 52, resize: 'vertical' })} value={modal.notes || ''} onChange={function(e) { setField('notes')(e.target.value); }} placeholder="Where the rates came from, when confirmed" /></div>

            {err && <div style={{ fontSize: 12, color: COLOR_ALERT, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {modal.id && <button style={Object.assign({}, btnDanger, { marginRight: 'auto' })} onClick={onDelete}>Delete</button>}
              <button style={btnGhost} onClick={function() { setModal(null); }}>Cancel</button>
              <button style={btnPrimary} onClick={onSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
